import { FormikErrors, FormikTouched, useFormikContext } from "formik";
import { values } from "lodash";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { countryCodes } from "../../constant/CountryCode";
import uploadFileIcon from "../../assets/images/uploadFileIcon.svg";
import PhoneInput from "react-phone-input-2";
import "./EasyApplyForm.css";
import "react-phone-input-2/lib/style.css";
import CountryCodeFlagDropdown from "../Common/CountryCodeFlagDropdown";

export interface PersonalInfoData {
  dob: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  city: string;
  phoneNumber: string;
  countryCode: string;
  profileImage: File | null;
  cvFile: File | null;
}

export interface PersonalInfoErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  confirmEmail?: string;
  phoneNumber?: string;
}

interface PersonalInfoProps {
  value: PersonalInfoData;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors?: FormikErrors<PersonalInfoData>;
  touched?: FormikTouched<PersonalInfoData>;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({
  value,
  errors,
  onChange,
  touched,
  onBlur,
}) => {
  const { setFieldValue, validateField, submitCount } = useFormikContext<{
    personalInfo: PersonalInfoData;
  }>();
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (!value.countryCode || value.countryCode.trim() === "") {
      let fallbackDialCode = "+44"; // Default to GB if locale detection fails
      try {
        const locale = navigator?.language || navigator?.languages?.[0];
        const region = locale?.split?.("-")?.[1]?.toUpperCase();
        if (region) {
          const byRegion = countryCodes.find((c) => c.code === region);
          if (byRegion) fallbackDialCode = byRegion.dial_code;
        }
      } catch (_) {}
      setFieldValue("personalInfo.countryCode", fallbackDialCode);
    }
  }, [value.countryCode, setFieldValue]);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFieldValue("personalInfo.profileImage", file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);

      await validateField("personalInfo.profileImage");
    } else {
      setImagePreview("");
    }
  };

  const handleCvChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFieldValue("personalInfo.cvFile", file);

    if (file) {
      await validateField("personalInfo.cvFile");
    }
  };

  return (
    <div className="form-section">
      <h4 className="section-title">Personal Information</h4>
      <p style={{ fontSize: "14px", color: "#6c757d", marginBottom: "20px" }}>
        Fields marked with <span className="required-field">*</span> are
        required.
      </p>

      {/* File Uploads */}
      <div className="row g-3 personal-upload-row">
        <div className="col-md-6">
          <label className="form-label">
            User image <span className="required-field">*</span>
          </label>
          <div
            className={`upload-area ${value.profileImage ? "has-file" : ""}`}
            onClick={() => {
              const input = document.getElementById(
                "profileImageInput"
              ) as HTMLInputElement | null;
              input?.click();
            }}
            style={{ position: "relative" }}
          >
            {imagePreview ? (
              <div className="upload-preview">
                <img src={imagePreview} alt="Preview" />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setFieldValue("personalInfo.profileImage", null);
                    setImagePreview("");
                  }}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "rgba(255,255,255,0.8)",
                    borderRadius: 6,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#4E4E4E",
                    fontWeight: 600,
                  }}
                >
                  ×
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <img src={uploadFileIcon} alt="upload" />
                <div style={{ marginTop: 8, color: "#4E4E4E" }}>
                  Drag a file or{" "}
                  <span style={{ textDecoration: "underline" }}>select</span>
                </div>
              </div>
            )}
          </div>
          <input
            id="profileImageInput"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
          {value.profileImage && !imagePreview && (
            <div className="file-actions">
              <div className="file-name">{value.profileImage.name}</div>
            </div>
          )}
          {(errors as any)?.profileImage &&
            (submitCount > 0 || value.profileImage) && (
              <div className="error-message">
                {(errors as any)?.profileImage}
              </div>
            )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            User CV <span className="required-field">*</span>
          </label>
          <div
            className={`upload-area ${value.cvFile ? "has-file" : ""}`}
            onClick={() => {
              const input = document.getElementById(
                "cvFileInput"
              ) as HTMLInputElement | null;
              input?.click();
            }}
            style={{ position: "relative" }}
          >
            {value.cvFile ? (
              <>
                <div className="file-name" style={{ textAlign: "center" }}>
                  {value.cvFile.name}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setFieldValue("personalInfo.cvFile", null);
                  }}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "rgba(255,255,255,0.8)",
                    borderRadius: 6,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#4E4E4E",
                    fontWeight: 600,
                  }}
                >
                  ×
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center" }}>
                <img src={uploadFileIcon} alt="upload" />
                <div style={{ marginTop: 8, color: "#4E4E4E" }}>
                  Drag a file or{" "}
                  <span style={{ textDecoration: "underline" }}>select</span>
                </div>
                <div
                  style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}
                ></div>
              </div>
            )}
          </div>
          <input
            id="cvFileInput"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: "none" }}
            onChange={handleCvChange}
          />
          {value.cvFile && !imagePreview && (
            <div className="file-actions">
              <div className="file-name">{value.cvFile.name}</div>
            </div>
          )}
          {(errors as any)?.cvFile && (submitCount > 0 || value.cvFile) && (
            <div className="error-message">{(errors as any)?.cvFile}</div>
          )}
        </div>
      </div>

      {/* Form Fields */}
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">
            First name <span className="required-field">*</span>
          </label>
          <input
            type="text"
            name="personalInfo.firstName"
            value={value.firstName}
            className={`form-control ${
              touched?.firstName && errors?.firstName ? "is-invalid" : ""
            }`}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter First Name"
          />
          {touched?.firstName && errors?.firstName && (
            <div className="error-message">{errors.firstName}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Last name <span className="required-field">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${
              touched?.lastName && errors?.lastName ? "is-invalid" : ""
            }`}
            name="personalInfo.lastName"
            value={value.lastName}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter Last Name"
          />
          {touched?.lastName && errors?.lastName && (
            <div className="error-message">{errors.lastName}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Email <span className="required-field">*</span>
          </label>
          <input
            type="email"
            className={`form-control ${
              touched?.email && errors?.email ? "is-invalid" : ""
            }`}
            name="personalInfo.email"
            value={value.email}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter Email"
          />
          {touched?.email && errors?.email && (
            <div className="error-message">{errors.email}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Confirm your Email <span className="required-field">*</span>
          </label>
          <input
            type="email"
            className={`form-control ${
              touched?.confirmEmail && errors?.confirmEmail ? "is-invalid" : ""
            }`}
            name="personalInfo.confirmEmail"
            value={value.confirmEmail}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter Confirm Email"
          />
          {touched?.confirmEmail && errors?.confirmEmail && (
            <div className="error-message">{errors.confirmEmail}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">City</label>
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              name="personalInfo.city"
              value={value.city}
              onChange={(e) => {
                const regex = /^[A-Za-z\s]*$/;
                if (regex.test(e.target.value)) {
                  onChange(e);
                }
              }}
              onBlur={onBlur}
              placeholder="Enter City"
            />
            <div className="position-absolute end-0 top-50 translate-middle-y me-2"></div>
          </div>
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Phone Number<span className="required-field">*</span>
          </label>
          <div className="phone-field-wrapper">
            <CountryCodeFlagDropdown
              value={value.countryCode}
              onChange={(countryCode) => {
                setFieldValue("personalInfo.countryCode", countryCode);
              }}
            />
            <input
              type="tel"
              className={`form-control ${
                touched?.phoneNumber && errors?.phoneNumber ? "is-invalid" : ""
              }`}
              name="personalInfo.phoneNumber"
              value={value.phoneNumber}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Enter Phone Number"
              onKeyDown={(e) => {
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ flex: 1 }}
            />
          </div>
          {touched?.phoneNumber && errors?.phoneNumber && (
            <div className="error-message">{errors.phoneNumber}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;
